import React, { useState, useCallback } from "react";
import { Tooltip } from "antd";
import { Camera } from "feather-icons-react/build/IconComponents";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import { useTranslation } from "react-i18next";
import CameraLocationModal from "../../CameraLocationModal";

const Shelf = ({
  id,
  rackId,
  rackName,
  name,
  sensors = [],
  cameras = [],
  cameras_location = {},
  // Read-only prop
  readOnly = false,
}) => {
  const [showCameraLocations, setShowCameraLocations] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [tempWeight, setTempWeight] = useState("");

  const { t } = useTranslation();

  // Get shelf number from name (remove "Shelf " prefix if exists)
  const getShelfNumber = useCallback(() => {
    const match = name?.match(/^Shelf (\d+)$/);
    return match ? match[1] : name || "";
  }, [name]);

  const totalProductCount = sensors.reduce(
    (productCount, sensor) => productCount + (sensor.products?.length || 0),
    0
  );

  const totalProductQuantity = sensors.reduce(
    (quantityCount, sensor) =>
      quantityCount +
      (sensor.products?.reduce(
        (sum, product) => sum + (parseInt(product.quantity) || 0),
        0
      ) || 0),
    0
  );

  // Weight editing handlers
  const handleWeightClick = (product) => {
    if (!readOnly) {
      setEditingProductId(product.id);
      setTempWeight(product.actual_zemic_sensor_weight || "0");
    }
  };

  const handleWeightSave = (productId) => {
    // Here you would typically call an API to save the weight
    // For now, we'll just exit edit mode
    setEditingProductId(null);
    setTempWeight("");
  };

  const handleSensorKeyPress = (e, productId) => {
    if (e.key === "Enter") {
      handleWeightSave(productId);
    } else if (e.key === "Escape") {
      setEditingProductId(null);
      setTempWeight("");
    }
  };

  // Camera locations handler (read-only mode)
  const handleViewCameraLocations = () => {
    if (!readOnly) {
      setShowCameraLocations(true);
    }
  };

  return (
    <div
      className="shelf-container mb-2"
      style={{
        backgroundColor: "#ffffff",
        border: "1px solid #e8ecf0",
        borderRadius: "8px",
        padding: "10px 12px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        transition: "all 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.target.style.borderColor = "#d1d7dd";
      }}
      onMouseLeave={(e) => {
        e.target.style.borderColor = "#e8ecf0";
      }}
    >
      {/* Shelf Header */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          {/* Display-only Shelf Name */}
          <h6
            className="mb-0 d-flex align-items-center"
            style={{
              fontSize: "13px",
              fontWeight: "600",
              color: "#495057",
              padding: "2px 4px",
              borderRadius: "4px",
            }}
          >
            {name}
          </h6>

          {/* Stats */}
          <span
            className="text-muted ms-2"
            style={{ fontSize: "10px", lineHeight: "1.2" }}
          >
            ({t("shelf.sensors")}: <strong>{sensors.length}</strong>,{" "}
            {t("shelf.product")}: <strong>{totalProductCount}</strong>,{" "}
            {t("shelf.totalQuantity")}: <strong>{totalProductQuantity}</strong>)
          </span>
        </div>

        {/* View-only Action Buttons */}
        <div className="d-flex align-items-center" style={{ gap: "6px" }}>
          {cameras.length > 0 && Object.keys(cameras_location).length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{ fontSize: "12px", color: "#495057", fontWeight: 500 }}
              >
                {cameras.map((camera, idx) => {
                  const location = cameras_location[camera.id];
                  return (
                    <span
                      key={camera.id}
                      style={{ marginRight: idx < cameras.length - 1 ? 8 : 0 }}
                    >
                      {camera.title}:
                      {location && (
                        <span style={{ color: "#888", marginLeft: 4 }}>
                          {location}
                        </span>
                      )}
                      {idx < cameras.length - 1 && <span>, </span>}
                    </span>
                  );
                })}
              </span>
            </div>
          )}
        </div>
      </div>
      <div
        className="sensors-row"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          alignItems: "flex-start",
        }}
      >
        {sensors.length === 0 ? (
          <div
            className="text-center text-muted w-100"
            style={{
              padding: "20px",
              fontSize: "12px",
              fontStyle: "italic",
            }}
          >
            No sensors configured for this shelf
          </div>
        ) : (
          sensors.map((sensor, index) => (
            <div
              key={sensor.sensor_number}
              className="sensor-wrapper"
              style={{
                position: "relative",
                display: "inline-block",
                minWidth: "130px",
              }}
            >
              <div
                className="sensor-display"
                style={{
                  textAlign: "center",
                  width: "100%",
                  border: "1px solid #dee2e6",
                  borderRadius: "6px",
                  padding: "8px",
                  backgroundColor: "#f8f9fa",
                  cursor: "default",
                }}
              >
                <div className="mt-3">
                  {sensor.products && sensor.products.length > 0 ? (
                    <div
                      className="d-flex flex-wrap gap-3"
                      style={{
                        fontSize: "12px",
                        marginTop: "5px",
                        color: "#555",
                      }}
                    >
                      {sensor.products.map((product, productIndex) => (
                        <div
                          key={`sensor_products${product.id}`}
                          className="d-flex flex-column align-items-center"
                        >
                          {/* Product Image and Quantity */}
                          <div className="d-flex align-items-center mb-1">
                            <div
                              title={`LM Number: ${product?.lm_product_number} \nEAN: ${product?.ean}\nName: ${product?.name}`}
                            >
                              <ImageWithBasePath
                                type="link"
                                src={product?.main_product_image}
                                alt="Product"
                                style={{
                                  width: 30,
                                  height: 30,
                                  marginRight: 5,
                                  borderRadius: "4px",
                                  objectFit: "cover",
                                }}
                              />
                            </div>
                            <span className="me-1">x</span>
                            <span className="fw-bolder">
                              {product.quantity || 0}
                            </span>
                          </div>

                          {/* Editable Weight */}
                          <div
                            className="text-center"
                            style={{ minWidth: "60px" }}
                            onClick={(e) => e.stopPropagation()} // Prevent parent button click
                          >
                            {editingProductId === product.id ? (
                              <div className="d-flex flex-column align-items-center">
                                <input
                                  type="number"
                                  value={tempWeight}
                                  onChange={(e) => setTempWeight(e.target.value)}
                                  onKeyDown={(e) =>
                                    handleSensorKeyPress(e, product.id)
                                  }
                                  onBlur={() => handleWeightSave(product.id)}
                                  autoFocus
                                  style={{
                                    width: "50px",
                                    height: "20px",
                                    border: "1px solid #007bff",
                                    borderRadius: "3px",
                                    textAlign: "center",
                                    fontSize: "10px",
                                    outline: "none",
                                    padding: "2px",
                                  }}
                                  step="0.01"
                                  min="0"
                                />
                              </div>
                            ) : (
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleWeightClick(product);
                                }}
                                style={{
                                  cursor: readOnly ? "default" : "pointer",
                                  padding: "2px 4px",
                                  borderRadius: "3px",
                                  backgroundColor: "#f8f9fa",
                                  border: "1px solid transparent",
                                  fontSize: "10px",
                                  minHeight: "16px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  if (!readOnly) {
                                    e.target.style.backgroundColor = "#e9ecef";
                                    e.target.style.borderColor = "#dee2e6";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!readOnly) {
                                    e.target.style.backgroundColor = "#f8f9fa";
                                    e.target.style.borderColor = "transparent";
                                  }
                                }}
                                title={readOnly ? `Weight: ${product.actual_zemic_sensor_weight || "0"}g` : "Click to edit weight"}
                              >
                                {product.actual_zemic_sensor_weight || "0"}g
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p
                      className="text-muted"
                      style={{
                        fontSize: "12px",
                        marginTop: "21px",
                        marginBottom: "10px",
                        color: "#555",
                        fontStyle: "italic",
                      }}
                    >
                      {t("shelf.noProductAdded")}
                    </p>
                  )}
                  <hr className="mt-3 mb-1" />
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: "600",
                      color: "#6c757d",
                    }}
                  >
                    Sensor {sensor.sensor_number}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Camera Location Modal - View Only */}
      {showCameraLocations && (
        <CameraLocationModal
          isOpen={showCameraLocations}
          onClose={() => setShowCameraLocations(false)}
          cameras={cameras}
          existingLocations={cameras_location}
          readOnly={true}
        />
      )}
    </div>
  );
};

export default Shelf;